import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

const labels = {
  en: {
    title: "Want to see how your team compares?",
    subtitle: "Use AI-powered assessments to build high-performing teams.",
    cta: "Start Free Trial",
    stats: [
      { value: "73%", label: "Fewer Bad Hires" },
      { value: "89%", label: "Prediction Accuracy" },
    ]
  },
  pt: {
    title: "Quer ver como sua equipe se compara?",
    subtitle: "Use avaliações com IA para construir equipes de alta performance.",
    cta: "Começar Teste Grátis",
    stats: [
      { value: "73%", label: "Menos Contratações Erradas" },
      { value: "89%", label: "Precisão de Previsão" },
    ]
  },
  es: {
    title: "¿Quieres ver cómo se compara tu equipo?",
    subtitle: "Usa evaluaciones con IA para construir equipos de alto rendimiento.",
    cta: "Iniciar Prueba Gratis",
    stats: [
      { value: "73%", label: "Menos Malas Contrataciones" },
      { value: "89%", label: "Precisión de Predicción" },
    ]
  },
};

const SignupHook: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = (() => {
    try { return useAuth(); } catch { return { user: null }; }
  })();

  // Don't show if user is authenticated
  if (user) return null;

  const uiLabels = labels[language as keyof typeof labels] || labels.en;

  return (
    <section className="px-4 md:px-8 lg:px-16 py-12">
      <div className="max-w-4xl mx-auto">
        <div className="bg-muted/50 border border-border rounded-xl p-8 md:p-10">
          <div className="text-center space-y-4">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              {uiLabels.title}
            </h3>
            <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto">
              {uiLabels.subtitle}
            </p>
            
            {/* Stats */}
            <div className="flex justify-center gap-8 py-4">
              {uiLabels.stats.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-foreground">{stat.value}</div>
                  <div className="text-xs md:text-sm text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
            
            <Button
              onClick={() => navigate('/signup')}
              size="lg"
              className="h-12 px-8 text-base font-semibold"
            >
              {uiLabels.cta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignupHook;
